const _jsxFileName = '';
import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Save,
  TrendingUp,
  RefreshCw,
  Calculator,
  Globe,
  ExternalLink,
  AlertCircle,
  CheckCircle,
  Clock,
  Edit3,
  X,
} from 'lucide-react';

const AdminPricing = ({ rates = [], onUpdateRates, onNavigate, user }) => {
  const [localRates, setLocalRates] = useState([]);
  const [originalRates, setOriginalRates] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [activeMetal, setActiveMetal] = useState('gold');

  // API base URL
  const API_BASE_URL = import.meta.env.VITE_API_URL;
  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('sparklingToken')}`,
  });

  // Fetch rates from database on component mount
  useEffect(() => {
    fetchRatesFromDatabase();
  }, []);

  // Fetch rates from database
  const fetchRatesFromDatabase = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/gold-rates`, { headers: authHeaders() });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();

      // Handle different response structures
      const ratesArray = data.rates || data.data || data;

      if (Array.isArray(ratesArray) && ratesArray.length > 0) {
        // Handle both camelCase and snake_case responses
        const formattedRates = ratesArray.map((rate) => ({
          id: rate.id,
          goldType: rate.goldType || rate.gold_type || '',
          karat: rate.karat,
          pricePerGram: parseFloat(rate.pricePerGram || rate.price_per_gram || 0),
          lastUpdated: rate.lastUpdated || rate.last_updated || new Date().toISOString(),
          metalType: rate.metal_type || rate.metalType || 'gold',
        }));

        setLocalRates(formattedRates);
        setOriginalRates(JSON.parse(JSON.stringify(formattedRates))); // Deep copy
      } else if (rates.length > 0) {
        // Fallback to props
        setLocalRates(rates);
        setOriginalRates(JSON.parse(JSON.stringify(rates)));
      } else {
        setLocalRates([]);
        setOriginalRates([]);
      }
    } catch (error) {
      setError('Failed to load gold rates. Please try again.');

      // Fallback to props if available
      if (rates.length > 0) {
        setLocalRates(rates);
        setOriginalRates(JSON.parse(JSON.stringify(rates)));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Save rates to database
  const saveRatesToDatabase = async (updatedRates) => {
    try {
      // Prepare data for API - convert to snake_case for backend
      const ratesForApi = updatedRates.map((rate) => ({
        id: rate.id,
        gold_type: rate.goldType,
        karat: rate.karat,
        price_per_gram: rate.pricePerGram,
      }));

      const response = await fetch(`${API_BASE_URL}/gold-rates/update`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ rates: ratesForApi }),
      });

      const data = await response.json();
      if (!data.success) return false;

      // Auto-recalculate all product prices based on new gold rates
      await fetch(`${API_BASE_URL}/gold-rates/recalculate-prices`, {
        method: 'POST',
        headers: authHeaders(),
      });

      return true;
    } catch (error) {
      return false;
    }
  };

  // Handle price change
  const handlePriceChange = (id, newPrice) => {
    const price = parseFloat(newPrice);
    if (isNaN(price) || price < 0) return;

    setLocalRates((prev) =>
      prev.map((rate) => (rate.id === id ? { ...rate, pricePerGram: price } : rate))
    );
  };

  // Handle edit start
  const handleEditStart = (rate) => {
    setEditingId(rate.id);
    setEditValue(rate.pricePerGram.toString());
  };

  // Handle edit cancel
  const handleEditCancel = () => {
    setEditingId(null);
    setEditValue('');
  };

  // Handle edit save
  const handleEditSave = (id) => {
    const price = parseFloat(editValue);
    if (!isNaN(price) && price >= 0) {
      handlePriceChange(id, editValue);
    }
    setEditingId(null);
  };

  // Handle key press (Enter to save, Escape to cancel)
  const handleKeyDown = (e, id) => {
    if (e.key === 'Enter') {
      handleEditSave(id);
    } else if (e.key === 'Escape') {
      handleEditCancel();
    }
  };

  // Check if there are unsaved changes (only for active metal)
  const hasChanges = () => {
    return activeRates.some((rate) => {
      const original = originalRates.find((r) => r.id === rate.id);
      return original && Math.abs(rate.pricePerGram - original.pricePerGram) > 0.01;
    });
  };

  // Get changed rates (only for active metal)
  const getChangedRates = () => {
    return activeRates.filter((rate) => {
      const original = originalRates.find((r) => r.id === rate.id);
      return original && Math.abs(rate.pricePerGram - original.pricePerGram) > 0.01;
    });
  };

  // Reset to original values
  const handleReset = () => {
    if (window.confirm('Discard all unsaved changes?')) {
      setLocalRates(JSON.parse(JSON.stringify(originalRates)));
      setEditingId(null);
      setError(null);
      setSuccess(null);
    }
  };

  // Save all changes
  const handleSave = async () => {
    if (!hasChanges()) {
      setSuccess('No changes to save');
      setTimeout(() => setSuccess(null), 3000);
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const changedRates = getChangedRates();

      // Save to database
      const saved = await saveRatesToDatabase(changedRates);

      if (saved) {
        // Update timestamps
        const now = new Date().toISOString();
        const updatedWithTimestamp = localRates.map((rate) => ({
          ...rate,
          lastUpdated: now,
        }));

        setLocalRates(updatedWithTimestamp);
        setOriginalRates(JSON.parse(JSON.stringify(updatedWithTimestamp)));
        setLastSaved(new Date());
        setSuccess(`Successfully updated ${changedRates.length} gold rate(s)`);

        // CRITICAL: Notify parent component to update products
        if (onUpdateRates) {
          onUpdateRates(updatedWithTimestamp);
        }

        // Auto-hide success message after 5 seconds
        setTimeout(() => setSuccess(null), 5000);
      } else {
        throw new Error('Database update failed');
      }
    } catch (error) {
      setError('Failed to update gold rates. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Filter by active metal then group
  const activeRates = localRates.filter((r) => (r.metalType || 'gold') === activeMetal);
  const groupedRates = activeRates.reduce((acc, rate) => {
    const key = rate.goldType || 'Other';
    if (!acc[key]) acc[key] = [];
    acc[key].push(rate);
    return acc;
  }, {});

  // Sort karats in logical order
  const sortKarats = (rates) => {
    const karatOrder = {
      '24K': 1,
      '24k': 1,
      '22K': 2,
      '22k': 2,
      '21K': 3,
      '21k': 3,
      '20K': 4,
      '20k': 4,
      '18K': 5,
      '18k': 5,
      '14K': 6,
      '14k': 6,
      '12K': 7,
      '12k': 7,
      '10K': 8,
      '10k': 8,
    };

    return [...rates].sort((a, b) => {
      const orderA = karatOrder[a.karat] || 999;
      const orderB = karatOrder[b.karat] || 999;
      return orderA - orderB;
    });
  };

  // Format gold type for display
  const formatGoldType = (type) => {
    const typeMap = {
      'Saudi Gold': 'Saudi Gold',
      'Japan Gold': 'Japan Gold',
      'Italian Gold': 'Italian Gold',
      'China Gold': 'China Gold',
      'Local PH Gold': 'Local PH Gold',
      'Ongpin Gold': 'Ongpin Gold',
    };
    return typeMap[type] || type;
  };

  // Get gold type color
  const getGoldTypeColor = (type) => {
    const colorMap = {
      'Saudi Gold': 'bg-amber-50 text-amber-700 border-amber-200',
      'Japan Gold': 'bg-stone-50 text-stone-700 border-stone-200',
      'Italian Gold': 'bg-rose-50 text-rose-700 border-rose-200',
      'China Gold': 'bg-slate-50 text-slate-700 border-slate-200',
      'Local PH Gold': 'bg-emerald-50 text-emerald-700 border-emerald-200',
      'Ongpin Gold': 'bg-gold-50 text-gold-700 border-gold-200',
    };
    return colorMap[type] || 'bg-stone-50 text-stone-700 border-stone-200';
  };

  // Loading state
  if (isLoading) {
    return React.createElement(
      'div',
      {
        className: 'pt-44 pb-12 min-h-screen bg-stone-50',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 297 },
      },
      React.createElement(
        'div',
        {
          className: 'max-w-6xl mx-auto px-6',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 298 },
        },
        React.createElement(
          'div',
          {
            className: 'flex justify-center items-center h-64',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 299 },
          },
          React.createElement(
            'div',
            {
              className: 'text-center',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 300 },
            },
            React.createElement(RefreshCw, {
              size: 32,
              className: 'animate-spin text-gold-600 mx-auto mb-4',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 301 },
            }),
            React.createElement(
              'p',
              {
                className: 'text-stone-500',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 302 },
              },
              'Loading gold rates...'
            )
          )
        )
      )
    );
  }

  return React.createElement(
    'div',
    {
      className: 'pt-44 pb-12 min-h-screen bg-stone-50',
      __self: this,
      __source: { fileName: _jsxFileName, lineNumber: 311 },
    },
    React.createElement(
      'div',
      {
        className: 'max-w-6xl mx-auto px-6',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 312 },
      },

      /* Header */
      React.createElement(
        'div',
        {
          className:
            'flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 315 },
        },
        React.createElement(
          'div',
          { __self: this, __source: { fileName: _jsxFileName, lineNumber: 316 } },
          (user?.role === 'admin' || (user?.permissions || []).includes('view_dashboard')) &&
            React.createElement(
              'button',
              {
                onClick: () => onNavigate('admin-dashboard'),
                className:
                  'flex items-center gap-2 text-stone-500 hover:text-stone-900 text-xs uppercase tracking-widest mb-2 transition-colors group',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 317 },
              },

              React.createElement(ArrowLeft, {
                size: 14,
                className: 'group-hover:-translate-x-1 transition-transform',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 321 },
              }),
              'Back to Dashboard'
            ),
          React.createElement(
            'h1',
            { className: 'font-serif text-3xl text-stone-900 flex items-center gap-3' },
            activeMetal === 'gold' ? 'Daily Gold Pricing' : 'Daily Silver Pricing',
            React.createElement(TrendingUp, { size: 24, className: 'text-gold-600' })
          ),
          React.createElement(
            'p',
            { className: 'text-stone-500 font-light mt-2 max-w-xl text-sm' },
            'Adjusting rates here will instantly update all jewelry items based on weight (grams).'
          ),
          React.createElement(
            'div',
            { className: 'flex gap-2 mt-4' },
            React.createElement(
              'button',
              {
                onClick: () => setActiveMetal('gold'),
                className: `px-5 py-2 text-xs uppercase tracking-widest font-bold border transition-colors ${activeMetal === 'gold' ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-stone-600 border-stone-300 hover:border-amber-400'}`,
              },
              '✨ Gold'
            ),
            React.createElement(
              'button',
              {
                onClick: () => setActiveMetal('silver'),
                className: `px-5 py-2 text-xs uppercase tracking-widest font-bold border transition-colors ${activeMetal === 'silver' ? 'bg-slate-500 text-white border-slate-500' : 'bg-white text-stone-600 border-stone-300 hover:border-slate-400'}`,
              },
              '◇ Silver'
            )
          )
        ),

        React.createElement(
          'div',
          {
            className: 'flex flex-col md:flex-row items-end md:items-center gap-4',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 333 },
          },
          /* Live Market Link — changes based on active metal */
          React.createElement(
            'a',
            {
              href:
                activeMetal === 'silver'
                  ? 'https://www.kitco.com/charts/silver'
                  : 'https://www.kitco.com/charts/gold',
              target: '_blank',
              rel: 'noopener noreferrer',
              className: `group flex items-center gap-3 px-5 py-3 bg-white rounded-sm hover:shadow-md transition-all duration-300 ${activeMetal === 'silver' ? 'border border-slate-200 hover:border-slate-400' : 'border border-gold-200 hover:border-gold-500'}`,
            },

            React.createElement(
              'div',
              {
                className: `p-2 rounded-full transition-colors ${activeMetal === 'silver' ? 'bg-slate-50 text-slate-500 group-hover:bg-slate-500 group-hover:text-white' : 'bg-gold-50 text-gold-600 group-hover:bg-gold-600 group-hover:text-white'}`,
              },
              React.createElement(Globe, { size: 18 })
            ),
            React.createElement(
              'div',
              { className: 'text-left' },
              React.createElement(
                'span',
                {
                  className: 'block text-[10px] text-stone-400 uppercase tracking-wider font-bold',
                },
                'Market Watch'
              ),
              React.createElement(
                'div',
                { className: 'flex items-center gap-1 text-stone-900 font-serif text-sm' },
                activeMetal === 'silver' ? 'Check Live Silver Price' : 'Check Live Gold Price',
                React.createElement(ExternalLink, { size: 12, className: 'text-stone-400' })
              )
            )
          ),

          /* Action Buttons */
          React.createElement(
            'div',
            {
              className: 'flex gap-2',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 353 },
            },
            hasChanges() &&
              React.createElement(
                'button',
                {
                  onClick: handleReset,
                  className:
                    'px-4 py-3 bg-white border border-stone-300 text-stone-600 text-xs uppercase tracking-widest hover:bg-stone-50 transition-colors flex items-center gap-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 355 },
                },

                React.createElement(X, {
                  size: 14,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 359 },
                }),
                ' Cancel'
              ),
            React.createElement(
              'button',
              {
                onClick: handleSave,
                disabled: isSaving || !hasChanges(),
                className:
                  'px-8 py-3 bg-stone-900 text-white text-xs uppercase tracking-widest hover:bg-gold-600 transition-colors flex items-center gap-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 362 },
              },

              isSaving
                ? React.createElement(RefreshCw, {
                    size: 16,
                    className: 'animate-spin',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 368 },
                  })
                : React.createElement(Save, {
                    size: 16,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 370 },
                  }),
              isSaving
                ? 'Saving...'
                : hasChanges()
                  ? `Save ${getChangedRates().length} Change${getChangedRates().length !== 1 ? 's' : ''}`
                  : 'Saved'
            )
          )
        )
      ),

      /* Status Messages */
      error &&
        React.createElement(
          'div',
          {
            className:
              'mb-6 bg-red-50 border border-red-200 text-red-700 p-4 rounded-sm flex items-center gap-3',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 380 },
          },
          React.createElement(AlertCircle, {
            size: 20,
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 381 },
          }),
          React.createElement(
            'span',
            {
              className: 'text-sm',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 382 },
            },
            error
          )
        ),

      success &&
        React.createElement(
          'div',
          {
            className:
              'mb-6 bg-green-50 border border-green-200 text-green-700 p-4 rounded-sm flex items-center gap-3',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 387 },
          },
          React.createElement(CheckCircle, {
            size: 20,
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 388 },
          }),
          React.createElement(
            'span',
            {
              className: 'text-sm',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 389 },
            },
            success
          )
        ),

      lastSaved &&
        React.createElement(
          'div',
          {
            className: 'mb-6 flex justify-end',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 394 },
          },
          React.createElement(
            'span',
            {
              className:
                'text-[10px] text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-100 flex items-center gap-2',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 395 },
            },
            React.createElement(Clock, {
              size: 10,
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 396 },
            }),
            'Last updated: ',
            lastSaved.toLocaleTimeString()
          )
        ),

      /* Summary Card */
      React.createElement(
        'div',
        {
          className:
            'bg-gradient-to-r from-gold-50 to-stone-50 border border-gold-200 p-6 rounded-sm mb-8',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 403 },
        },
        React.createElement(
          'div',
          {
            className: 'flex items-center gap-4 mb-4',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 404 },
          },
          React.createElement(Calculator, {
            className: 'text-gold-600',
            size: 24,
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 405 },
          }),
          React.createElement(
            'h2',
            {
              className: 'font-serif text-xl text-stone-900',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 406 },
            },
            'Price Calculation Formula'
          )
        ),
        React.createElement(
          'div',
          {
            className: 'grid grid-cols-1 md:grid-cols-3 gap-6 text-sm',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 408 },
          },
          React.createElement(
            'div',
            {
              className: 'bg-white p-4 rounded-sm border border-stone-200',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 409 },
            },
            React.createElement(
              'span',
              {
                className: 'block text-[10px] uppercase tracking-widest text-stone-400 mb-1',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 410 },
              },
              'Formula'
            ),
            React.createElement(
              'code',
              {
                className: 'text-stone-900 font-mono',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 411 },
              },
              'Price = Weight (g) \xD7 Gold Rate'
            )
          ),
          React.createElement(
            'div',
            {
              className: 'bg-white p-4 rounded-sm border border-stone-200',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 413 },
            },
            React.createElement(
              'span',
              {
                className: 'block text-[10px] uppercase tracking-widest text-stone-400 mb-1',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 414 },
              },
              'Example'
            ),
            React.createElement(
              'code',
              {
                className: 'text-stone-900 font-mono',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 415 },
              },
              '5g \xD7 ₱4,150 = ₱20,750'
            )
          ),
          React.createElement(
            'div',
            {
              className: 'bg-white p-4 rounded-sm border border-stone-200',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 417 },
            },
            React.createElement(
              'span',
              {
                className: 'block text-[10px] uppercase tracking-widest text-stone-400 mb-1',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 418 },
              },
              'Total Products'
            ),
            React.createElement(
              'span',
              {
                className: 'text-2xl font-serif text-stone-900',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 419 },
              },
              activeRates.length,
              ' rates'
            )
          )
        )
      ),

      /* Rates Grid */
      React.createElement(
        'div',
        {
          className: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 425 },
        },
        Object.entries(groupedRates).length > 0
          ? Object.entries(groupedRates).map(([type, typeRates]) =>
              React.createElement(
                'div',
                {
                  key: type,
                  className:
                    'bg-white border border-stone-200 shadow-sm rounded-sm overflow-hidden flex flex-col h-full',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 428 },
                },
                React.createElement(
                  'div',
                  {
                    className: `px-6 py-4 border-b border-stone-100 flex justify-between items-center ${getGoldTypeColor(type)}`,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 429 },
                  },
                  React.createElement(
                    'h3',
                    {
                      className: 'font-serif text-lg',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 430 },
                    },
                    formatGoldType(type)
                  ),
                  React.createElement(Calculator, {
                    size: 16,
                    className: 'opacity-50',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 431 },
                  })
                ),
                React.createElement(
                  'div',
                  {
                    className: 'p-6 space-y-4 flex-1',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 433 },
                  },
                  sortKarats(typeRates).map((rate) => {
                    const original = originalRates.find((r) => r.id === rate.id);
                    const isChanged =
                      original && Math.abs(rate.pricePerGram - original.pricePerGram) > 0.01;
                    const isEditing = editingId === rate.id;

                    return React.createElement(
                      'div',
                      {
                        key: rate.id,
                        className: 'flex items-center gap-4 group',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 440 },
                      },
                      React.createElement(
                        'div',
                        {
                          className: 'w-20 flex-shrink-0',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 441 },
                        },
                        React.createElement(
                          'div',
                          {
                            className: `py-2 px-3 border rounded text-xs font-bold text-center transition-colors ${
                              rate.karat.includes('24')
                                ? 'bg-gold-600 text-white border-gold-600'
                                : rate.karat.includes('22') || rate.karat.includes('21')
                                  ? 'bg-gold-50 text-gold-700 border-gold-200'
                                  : 'bg-stone-50 text-stone-600 border-stone-200'
                            }`,
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 442 },
                          },
                          rate.karat
                        )
                      ),

                      React.createElement(
                        'div',
                        {
                          className: 'flex-1 relative',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 451 },
                        },
                        React.createElement(
                          'label',
                          {
                            className:
                              'absolute -top-2 left-2 px-1 bg-white text-[9px] uppercase tracking-widest text-stone-400 font-bold',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 452 },
                          },
                          '₱/gram'
                        ),

                        isEditing
                          ? React.createElement(
                              'div',
                              {
                                className: 'flex items-center gap-1',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 457 },
                              },
                              React.createElement('input', {
                                type: 'number',
                                value: editValue,
                                onChange: (e) => setEditValue(e.target.value),
                                onKeyDown: (e) => handleKeyDown(e, rate.id),
                                onBlur: () => handleEditSave(rate.id),
                                step: '0.01',
                                min: '0',
                                className:
                                  'w-full border-2 border-gold-500 px-3 py-3 text-sm focus:outline-none rounded-sm',
                                autoFocus: true,
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 458 },
                              })
                            )
                          : React.createElement(
                              'div',
                              {
                                className: `w-full border px-3 py-3 text-sm flex items-center justify-between cursor-pointer hover:border-gold-500 transition-colors ${
                                  isChanged ? 'border-gold-500 bg-gold-50/50' : 'border-stone-200'
                                }`,
                                onClick: () => handleEditStart(rate),
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 471 },
                              },

                              React.createElement(
                                'span',
                                {
                                  className: 'font-mono',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 477 },
                                },
                                '₱',
                                rate.pricePerGram.toFixed(2)
                              ),
                              React.createElement(Edit3, {
                                size: 14,
                                className:
                                  'opacity-0 group-hover:opacity-100 transition-opacity text-stone-400',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 478 },
                              })
                            )
                      ),

                      isChanged &&
                        !isEditing &&
                        React.createElement('div', {
                          className: 'w-2 h-2 bg-gold-500 rounded-full animate-pulse',
                          title: 'Unsaved change',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 484 },
                        })
                    );
                  })
                )
              )
            )
          : React.createElement(
              'div',
              {
                className:
                  'col-span-full text-center py-12 bg-white border border-stone-200 rounded-sm',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 493 },
              },
              React.createElement(AlertCircle, {
                size: 48,
                className: 'mx-auto text-stone-400 mb-4',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 494 },
              }),
              React.createElement(
                'p',
                {
                  className: 'text-stone-500',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 495 },
                },
                'No gold rates found'
              ),
              React.createElement(
                'p',
                {
                  className: 'text-xs text-stone-400 mt-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 496 },
                },
                'Please add gold rates in the database'
              )
            )
      ),

      /* Info Box */
      React.createElement(
        'div',
        {
          className: 'mt-8 bg-blue-50 border border-blue-200 p-6 rounded-sm',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 502 },
        },
        React.createElement(
          'div',
          {
            className: 'flex items-start gap-3',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 503 },
          },
          React.createElement(AlertCircle, {
            size: 20,
            className: 'text-blue-600 flex-shrink-0 mt-0.5',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 504 },
          }),
          React.createElement(
            'div',
            { __self: this, __source: { fileName: _jsxFileName, lineNumber: 505 } },
            React.createElement(
              'h3',
              {
                className: 'text-sm font-bold text-blue-800 uppercase tracking-wider mb-2',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 506 },
              },
              'How Gold Pricing Works'
            ),
            React.createElement(
              'ul',
              {
                className: 'text-sm text-blue-700 space-y-2',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 509 },
              },
              React.createElement(
                'li',
                {
                  className: 'flex items-center gap-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 510 },
                },
                React.createElement('div', {
                  className: 'w-1 h-1 bg-blue-400 rounded-full',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 511 },
                }),
                'Product prices are automatically calculated based on: ',
                React.createElement(
                  'strong',
                  { __self: this, __source: { fileName: _jsxFileName, lineNumber: 512 } },
                  'Weight (g) \xD7 Gold Rate'
                )
              ),
              React.createElement(
                'li',
                {
                  className: 'flex items-center gap-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 514 },
                },
                React.createElement('div', {
                  className: 'w-1 h-1 bg-blue-400 rounded-full',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 515 },
                }),
                'Changing gold rates will update all affected products immediately in the shop'
              ),
              React.createElement(
                'li',
                {
                  className: 'flex items-center gap-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 518 },
                },
                React.createElement('div', {
                  className: 'w-1 h-1 bg-blue-400 rounded-full',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 519 },
                }),
                'Unsaved changes are highlighted with a ',
                React.createElement('span', {
                  className: 'w-2 h-2 bg-gold-500 rounded-full inline-block mx-1',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 520 },
                }),
                ' gold dot'
              ),
              React.createElement(
                'li',
                {
                  className: 'flex items-center gap-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 522 },
                },
                React.createElement('div', {
                  className: 'w-1 h-1 bg-blue-400 rounded-full',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 523 },
                }),
                React.createElement(
                  'strong',
                  { __self: this, __source: { fileName: _jsxFileName, lineNumber: 524 } },
                  'Note:'
                ),
                ' Products with "Manual Price Override" enabled will not be auto-updated'
              )
            )
          )
        )
      )
    )
  );
};

export default AdminPricing;
